/**
 * lrdiy-klaviyo-forms.js
 * Shared Klaviyo form submission handler for Liquid Rubber DIY
 * Handles both the Installation Quote form and Applicator Signup form.
 *
 * Uses window._learnq (Klaviyo's client-side JS API) which is already
 * loaded on the storefront via the Klaviyo integration.
 *
 * FIX 2026-06-08:
 * - Replaced form.elements['contact[name]'] with form.querySelector('[name="..."]')
 *   to reliably read bracket-notation field names across all browsers.
 * - Changed Klaviyo track event properties to underscore_keys so Klaviyo email
 *   templates can reference them via dot notation ({{ event.full_name }}).
 * - Added server-side backup: also submits to Shopify contact endpoint so a
 *   notification email always arrives regardless of Klaviyo's status.
 */

(function () {
  'use strict';

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function getLearnQ() {
    window._learnq = window._learnq || [];
    return window._learnq;
  }

  function fieldVal(form, name) {
    // FIX: use querySelector instead of form.elements[] for bracket-notation names.
    // form.elements['contact[x]'] is unreliable on mobile Safari and some older browsers.
    var el = form.querySelector('[name="' + name + '"]');
    return el ? (el.value || '').trim() : '';
  }

  function showSuccess(form, successEl) {
    form.style.display = 'none';
    successEl.removeAttribute('hidden');
    successEl.focus();
  }

  function showError(errorEl, message) {
    errorEl.textContent = message || 'Something went wrong. Please try again or contact us directly.';
    errorEl.removeAttribute('hidden');
    errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function hideError(errorEl) {
    errorEl.textContent = '';
    errorEl.setAttribute('hidden', '');
  }

  function serializeCheckboxGroup(form, name) {
    var checked = form.querySelectorAll('input[name="' + name + '"]:checked');
    return Array.from(checked).map(function (cb) { return cb.value; }).join(', ');
  }

  function setSubmitting(btn, isSubmitting) {
    btn.disabled = isSubmitting;
    btn.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    var label = btn.querySelector('.lrdiy-btn-label');
    var spinner = btn.querySelector('.lrdiy-btn-spinner');
    if (label) label.style.visibility = isSubmitting ? 'hidden' : '';
    if (spinner) spinner.removeAttribute(isSubmitting ? 'hidden' : '');
    if (spinner && !isSubmitting) spinner.setAttribute('hidden', '');
  }

  /**
   * Submit a copy of the data to Shopify's /contact endpoint as a backup.
   * This guarantees the store owner receives a Shopify notification email
   * with all field values in the body, independent of Klaviyo.
   *
   * @param {string} wrapperId  id of the <div> wrapping the hidden {% form 'contact' %}
   * @param {string} emailVal   submitter's email address
   * @param {string} nameVal    submitter's full name
   * @param {string} bodyVal    pre-formatted plain-text summary of all fields
   *
   * NOTE: We use a wrapper <div id="..."> rather than passing id: '...' to
   * Shopify's {% form %} tag — Shopify does not reliably set an HTML id attribute
   * via that parameter, so getElementById on the form element itself returns null.
   * The wrapper div is always a plain HTML element we fully control.
   */
  function submitBackupContactForm(wrapperId, emailVal, nameVal, bodyVal) {
    var wrapper = document.getElementById(wrapperId);
    // Find the <form> rendered by Shopify's {% form 'contact' %} inside the wrapper
    var backupForm = wrapper ? wrapper.querySelector('form') : null;
    if (!backupForm) {
      console.warn('[LRDIY] Backup contact form not found (#' + wrapperId + ')');
      return;
    }

    // Set input values directly on the DOM elements BEFORE creating FormData so
    // that the serialised FormData captures the populated values (not the empty defaults).
    var emailInput = backupForm.querySelector('[name="contact[email]"]');
    var nameInput  = backupForm.querySelector('[name="contact[name]"]');
    var bodyInput  = backupForm.querySelector('[name="contact[body]"]');
    if (emailInput) emailInput.value = emailVal;
    if (nameInput)  nameInput.value  = nameVal;
    if (bodyInput)  bodyInput.value  = bodyVal;

    var formData = new FormData(backupForm);

    fetch('/contact', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData,
    }).catch(function (err) {
      // Non-critical — Klaviyo is the primary channel; log but don't surface to user
      console.warn('[LRDIY] Backup contact form submission failed:', err);
    });
  }

  // ─── Quote Form ──────────────────────────────────────────────────────────────

  function initQuoteForm() {
    var form = document.getElementById('lrdiy-quote-form');
    if (!form) return;

    var submitBtn = form.querySelector('[data-submit-btn]');
    var successEl = document.getElementById('lrdiy-quote-success');
    var errorEl = document.getElementById('lrdiy-quote-error');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      hideError(errorEl);

      // Honeypot check — if filled, silently reject (bot detected)
      var honeypot = form.querySelector('[name="contact[_hp]"]');
      if (honeypot && honeypot.value !== '') {
        showSuccess(form, successEl);
        return;
      }

      // reCAPTCHA check
      if (typeof grecaptcha !== 'undefined' && grecaptcha.getResponse() === '') {
        showError(errorEl, 'Please complete the reCAPTCHA check to continue.');
        return;
      }

      var jobTypes = serializeCheckboxGroup(form, 'contact[job_type][]');

      // FIX: use fieldVal() helper — querySelector-based, works across all browsers
      var data = {
        fullName:     fieldVal(form, 'contact[full_name]'),
        phone:        fieldVal(form, 'contact[phone]'),
        email:        fieldVal(form, 'contact[email]'),
        suburb:       fieldVal(form, 'contact[suburb]'),
        propertyType: fieldVal(form, 'contact[property_type]'),
        sqm:          fieldVal(form, 'contact[sqm]'),
        jobType:      jobTypes,
        description:  fieldVal(form, 'contact[description]'),
        timing:       fieldVal(form, 'contact[timing]'),
      };

      // Basic client-side validation
      if (!data.fullName || !data.email || !data.phone || !data.suburb || !data.jobType || !data.description) {
        showError(errorEl, 'Please fill in all required fields. Make sure to select at least one job type.');
        return;
      }

      setSubmitting(submitBtn, true);

      // ── Server-side backup via Shopify contact form ──────────────────────────
      // Sends all data to /contact so the store always receives a Shopify
      // notification email, independent of Klaviyo's availability.
      var backupBody = [
        'QUOTE REQUEST — ' + new Date().toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' }),
        '',
        'Name:              ' + data.fullName,
        'Email:             ' + data.email,
        'Phone:             ' + data.phone,
        'Suburb/Postcode:   ' + data.suburb,
        'Property Type:     ' + data.propertyType,
        'Est. Square Metres:' + (data.sqm || 'Not provided'),
        'Job Type(s):       ' + data.jobType,
        'Timing:            ' + (data.timing || 'Not specified'),
        '',
        'Project Description:',
        data.description,
      ].join('\n');

      submitBackupContactForm('lrdiy-quote-backup-wrapper', data.email, data.fullName, backupBody);

      // ── Klaviyo ──────────────────────────────────────────────────────────────
      try {
        var _learnq = getLearnQ();

        // Identify the contact in Klaviyo
        _learnq.push(['identify', {
          '$email':        data.email,
          '$first_name':   data.fullName.split(' ')[0] || data.fullName,
          '$last_name':    data.fullName.split(' ').slice(1).join(' ') || '',
          '$phone_number': data.phone,
        }]);

        // Property names match the existing Klaviyo template which uses |lookup: filter.
        // e.g. {{ event|lookup:'Full Name' }}, {{ event|lookup:'Suburb / Postcode' }}
        _learnq.push(['track', 'Quote Request Submitted', {
          'Full Name':        data.fullName,
          'Phone':            data.phone,
          'Email':            data.email,
          'Suburb / Postcode':data.suburb,
          'Property Type':    data.propertyType,
          'Estimated Sqm':    data.sqm,
          'Job Type':         data.jobType,
          'Project Description': data.description,
          'Timing':           data.timing,
          'Source':           'Installation Quote Page',
        }]);

        // Small delay to allow Klaviyo to fire before showing success
        setTimeout(function () {
          setSubmitting(submitBtn, false);
          showSuccess(form, successEl);
        }, 600);

      } catch (err) {
        setSubmitting(submitBtn, false);
        showError(errorEl, 'Unable to submit your request right now. Please try again shortly.');
        console.error('[LRDIY] Klaviyo quote form error:', err);
      }
    });
  }

  // ─── Applicator Signup Form ──────────────────────────────────────────────────

  function initApplicatorForm() {
    var form = document.getElementById('lrdiy-applicator-form');
    if (!form) return;

    var submitBtn = form.querySelector('[data-submit-btn]');
    var successEl = document.getElementById('lrdiy-applicator-success');
    var errorEl = document.getElementById('lrdiy-applicator-error');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      hideError(errorEl);

      // Honeypot check — if filled, silently reject (bot detected)
      var honeypot = form.querySelector('[name="contact[_hp]"]');
      if (honeypot && honeypot.value !== '') {
        showSuccess(form, successEl);
        return;
      }

      // reCAPTCHA check
      if (typeof grecaptcha !== 'undefined' && grecaptcha.getResponse() === '') {
        showError(errorEl, 'Please complete the reCAPTCHA check to continue.');
        return;
      }

      var jobTypes = serializeCheckboxGroup(form, 'contact[job_types][]');

      // FIX: use fieldVal() helper — querySelector-based, works across all browsers
      var data = {
        businessName: fieldVal(form, 'contact[business_name]'),
        contactName:  fieldVal(form, 'contact[contact_name]'),
        email:        fieldVal(form, 'contact[email]'),
        phone:        fieldVal(form, 'contact[phone]'),
        serviceAreas: fieldVal(form, 'contact[service_areas]'),
        tradeType:    fieldVal(form, 'contact[trade_type]'),
        experience:   fieldVal(form, 'contact[experience]'),
        jobScale:     fieldVal(form, 'contact[job_scale]'),
        jobTypes:     jobTypes,
        insurance:    fieldVal(form, 'contact[insurance]'),
        abn:          fieldVal(form, 'contact[abn]'),
        website:      fieldVal(form, 'contact[website]'),
        description:  fieldVal(form, 'contact[description]'),
      };

      // Basic validation
      if (!data.businessName || !data.contactName || !data.email || !data.phone ||
          !data.serviceAreas || !data.tradeType || !data.abn || !data.description) {
        showError(errorEl, 'Please fill in all required fields.');
        return;
      }

      setSubmitting(submitBtn, true);

      // ── Server-side backup via Shopify contact form ──────────────────────────
      var backupBody = [
        'APPLICATOR APPLICATION — ' + new Date().toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' }),
        '',
        'Business Name:  ' + data.businessName,
        'Contact Name:   ' + data.contactName,
        'Email:          ' + data.email,
        'Phone:          ' + data.phone,
        'ABN:            ' + data.abn,
        'Service Areas:  ' + data.serviceAreas,
        'Trade Type:     ' + data.tradeType,
        'Experience:     ' + (data.experience || 'Not specified'),
        'Job Scale:      ' + (data.jobScale || 'Not specified'),
        'Job Types:      ' + (data.jobTypes || 'Not specified'),
        'Insurance:      ' + (data.insurance || 'Not specified'),
        'Website:        ' + (data.website || 'None'),
        '',
        'Business Description:',
        data.description,
      ].join('\n');

      submitBackupContactForm('lrdiy-applicator-backup-wrapper', data.email, data.contactName, backupBody);

      // ── Klaviyo ──────────────────────────────────────────────────────────────
      try {
        var _learnq = getLearnQ();

        _learnq.push(['identify', {
          '$email':        data.email,
          '$first_name':   data.contactName.split(' ')[0] || data.contactName,
          '$last_name':    data.contactName.split(' ').slice(1).join(' ') || '',
          '$phone_number': data.phone,
          'Business Name': data.businessName,
        }]);

        _learnq.push(['track', 'Applicator Application Submitted', {
          'Business Name':            data.businessName,
          'Contact Name':             data.contactName,
          'Email':                    data.email,
          'Phone':                    data.phone,
          'Service Areas':            data.serviceAreas,
          'Trade Type':               data.tradeType,
          'Years of Experience':      data.experience,
          'Job Scale':                data.jobScale,
          'Job Types':                data.jobTypes,
          'Public Liability Insurance': data.insurance,
          'ABN':                      data.abn,
          'Website':                  data.website,
          'Business Description':     data.description,
          'Source':                   'Become an Applicator Page',
        }]);

        setTimeout(function () {
          setSubmitting(submitBtn, false);
          showSuccess(form, successEl);
        }, 600);

      } catch (err) {
        setSubmitting(submitBtn, false);
        showError(errorEl, 'Unable to submit your application right now. Please try again shortly.');
        console.error('[LRDIY] Klaviyo applicator form error:', err);
      }
    });
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initQuoteForm();
      initApplicatorForm();
    });
  } else {
    initQuoteForm();
    initApplicatorForm();
  }

})();
